import { Component, EventEmitter, Injector, Input, OnInit, Output, ChangeDetectionStrategy } from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { BodyComponentObject, CategoryTemplateMessage, StatusTemplateMessage, TemplateMessageObject, TemplateMessageObjectPager } from '@trendbuild/trend-cloud-api';
import { AbstractComponent, Channel, ChannelService, WaTemplateResponse } from 'lib-trend-core';
import { debounceTime, takeUntil } from 'rxjs';

export interface TemplateMessage {
  previewText: string;
  params: any[];
  imageFile: string;
}

@Component({
    selector: 'wa-template-select',
    templateUrl: './wa-template-select.v2.component.html',
    // templateUrl: './wa-template-select.component.html',
    styleUrls: ['./wa-template-select.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class WaTemplateSelectComponent extends AbstractComponent implements OnInit {
  @Input() set channel(channel: Channel) {
    this.selectedChannel = channel;
    if (!channel?._id) {
      this.loadSeq++;
      this.loading = false;
      this.loadingContent = false;
      this.templates = [];
      this.allTemplates = [];
      return;
    }
    this.listWaTemplates();
  }

  private loadSeq: number = 0;

  private _selectedTemplateId: string;
  @Input() set selectedTemplateId(id: string) {
    this._selectedTemplateId = id;
    if (id) {
      // Clear filters when template is selected
      this.searchControl.setValue('', { emitEvent: false });
      this.selectedCategory = '';
    }
    if (this.selectedChannel) {
      this.listWaTemplates();
    }
  }
  get selectedTemplateId(): string {
    return this._selectedTemplateId;
  }

  @Output() onSelectTemplate = new EventEmitter<TemplateMessageObject>();

  selectedTemplate: TemplateMessageObject | null = null;
  templates: Array<TemplateMessageObject> = new Array<TemplateMessageObject>()
  allTemplates: TemplateMessageObject[] = [];
  selectedChannel: Channel;
  form: FormGroup;
  searchControl: FormControl = new FormControl('');
  selectedCategory: string = '';
  selectedStatus: string = '';

  categories = [
    { value: 'MARKETING', label: 'Marketing' },
    { value: 'UTILITY', label: 'Utilitário' },
    { value: 'AUTHENTICATION', label: 'Autenticação' },
  ];

  statuses = [
    { value: 'APPROVED', label: 'Aprovado' },
    { value: 'PENDING', label: 'Pendente' },
    { value: 'REJECTED', label: 'Rejeitado' }
  ];

  constructor(
    injector: Injector,
    public channelService: ChannelService,
    public override formBuilder: FormBuilder,
  ) {
    super(injector);
    this.form = this.formBuilder.group({});
    this.setupSearchListener();
  }

  ngOnInit(): void {
    this.listWaTemplates();
  }

  private setupSearchListener() {
    this.searchControl.valueChanges
      .pipe(debounceTime(300))
      .subscribe((value: string) => {
        this.filterTemplates(value);
      });
  }

  filterByCategory(category: string) {
    if (this.selectedTemplateId) return;
    this.selectedCategory = category;
    this.applyFilters();
  }

  filterByStatus(status: string) {
    if (this.selectedTemplateId) return;
    this.selectedStatus = status;
    this.applyFilters();
  }

  filterTemplates(event: any) {
    if (this.selectedTemplateId) return;
    const value = typeof event === 'string' ? event : event?.target?.value || '';
    this.applyFilters(value);
  }

  private applyFilters(searchValue: string = this.searchControl.value) {
    let filteredTemplates = [...this.allTemplates];

    if (this.selectedCategory) {
      filteredTemplates = filteredTemplates.filter(template =>
        template.category === this.selectedCategory
      );
    }

    if (this.selectedStatus) {
      filteredTemplates = filteredTemplates.filter(template =>
        template.status === this.selectedStatus
      );
    }

    // Aplicar filtro de busca
    if (searchValue?.trim()) {
      const search = searchValue.toLowerCase();
      filteredTemplates = filteredTemplates.filter(template =>
        template.name.toLowerCase().includes(search) ||
        template.category?.toLowerCase().includes(search) ||
        (<BodyComponentObject>template.components?.find(c => c.type === 'BODY'))?.text?.toLowerCase().includes(search)
      );
    }

    this.templates = filteredTemplates;
  }

  async initTemplate(templateId) {
    await this.listWaTemplates();
    if (this.templates) {
      const template = this.templates.find(wat => wat.id === templateId);
      if (template) {
        this.onSelectTemplate.emit(template);
      }
    }
  }

  async listWaTemplates() {
    if (!this.selectedChannel) return;
    this.loading = true;

    const seq = ++this.loadSeq;
    const isCurrentLoad = () => seq === this.loadSeq;

    this.channelService.getTemplatesByChannel(this.selectedChannel._id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: async (templateMessageObjectPager: TemplateMessageObjectPager) => {
          if (!isCurrentLoad()) {
            return;
          }
          try {
            const allTemplates = templateMessageObjectPager.data || [];

            if (this.selectedTemplateId) {
              const selectedTemplate = allTemplates.find(template => template.id === this.selectedTemplateId);
              if (selectedTemplate) {
                this.templates = [selectedTemplate];
                this.allTemplates = [selectedTemplate];
              }
            } else {
              this.templates = allTemplates;
              this.allTemplates = allTemplates;
            }
            this.loading = false;
          } catch (error) {
            console.error('Error fetching templates:', error);
            this.templates = [];
            this.allTemplates = [];
          }
          this.loading = false;
        },
        error: (err) => {
          if (!isCurrentLoad()) {
            return;
          }
          this.loading = false;
          this.loadingContent = false;
          this.templates = [];
          this.allTemplates = [];
          this.alertService.error(err?.error?.message);
        },
        complete: () => {
          if (isCurrentLoad()) {
            this.loadingContent = false;
          }
        }
      });
  }

  showTemplateDetails(template: TemplateMessageObject) {
    this.selectedTemplateId = template.id;
    this.onSelectTemplate.emit(template);
  }

  clearTemplate() {
    this.selectedTemplateId = null;
    this.onSelectTemplate.emit(null);
  }

  setupForm() {
    this.form = this.formBuilder.group({});
  }

  selectVarContactName(index: number) {
    this.form.get(`var${index}`).setValue('Nome do Contato');
  }

  selectVarLinkChannel(index: number) {
    this.form.get(`var${index}`).setValue('Link canal Whatsapp');
  }

  selectVarOperatorName(index: number) {
    this.form.get(`var${index}`).setValue('Nome atendente');
  }

  selectVarDepartmentName(index: number) {
    this.form.get(`var${index}`).setValue('Departamento');
  }

  selectVarProtocol(index: number) {
    this.form.get(`var${index}`).setValue('Nº do protocolo');
  }

  public getBodyText(template: TemplateMessageObject): string {
    const bodyComponent = <BodyComponentObject>template?.components.find(c => c.type === 'BODY');
    return bodyComponent.text;
  }

  public cleanCacheTemplates() {
    this.channelService.cleanCacheTemplatesByChannel(this.selectedChannel._id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.templates = [];
          this.allTemplates = [];
          this.listWaTemplates();
        }
      });
  }

  translateTemplateStatus(status: StatusTemplateMessage | string): string {
    switch (status) {
      case 'PENDING':
        return 'Pendente';
      case 'APPROVED':
        return 'Aprovado';
      case 'REJECTED':
        return 'Rejeitado';
      default:
        return status ?? '';
    }
  }
  statusBadgeClass(status: StatusTemplateMessage | string): string {
    const variantes: { [key: string]: string } = {
      APPROVED: 'is-ok',
      PENDING: 'is-warn',
      PENDING_DELETION: 'is-warn',
      IN_APPEAL: 'is-run',
      REJECTED: 'is-err',
      PAUSED: 'is-err',
      LIMIT_EXCEEDED: 'is-err',
      DISABLED: 'is-neutral',
      DELETED: 'is-neutral',
      ARCHIVED: 'is-neutral',
    };
    return variantes[status] ?? 'is-neutral';
  }

  languageFlag(language: string): string {
    const flags: { [code: string]: string } = {
      pt_BR: 'assets/icons/br.svg',
      pt_PT: 'assets/icons/pt.svg',
      pt_PR: 'assets/icons/pt.svg',
      en: 'assets/icons/us.svg',
      en_US: 'assets/icons/us.svg',
    };
    return flags[language] ?? '';
  }

  translateTemplateCategory(type: CategoryTemplateMessage | string): string {
    switch (type) {
      case 'AUTHENTICATION':
        return 'Autenticação';
      case 'MARKETING':
        return 'Marketing';
      case 'UTILITY':
        return 'Utilitário';
      default:
        return '';
    }
  }


}
